import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import countryFlagsMap from './countryFlagsMap';

// Using Vite's import.meta.glob to handle all flags dynamically.
const FLAG_PATH = './flags/';

const CountryFlag = ({ countryName, alt = 'Country flag', className = 'w-6 h-4' }) => {
  const flagFile = countryFlagsMap[countryName] || 'xx.svg'; // Default to 'xx.svg' if no flag found
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [flagSrc, setFlagSrc] = useState(null);
  const flagRef = useRef(null);

  // Dynamically load flags using Vite's import.meta.glob
  const flags = import.meta.glob('./flags/*.svg');

  useEffect(() => {
    const flagPath = flags[`./flags/${flagFile}`];

    if (flagPath) {
      // Dynamically import the SVG image using Vite's system
      flagPath().then((module) => {
        setFlagSrc(module.default); // Get the URL from the module
      }).catch((err) => {
        console.error("Error loading the flag image", err);
        setFlagSrc(flags[`./flags/xx.svg`]()); // Fallback to 'xx.svg' if an error occurs
      });
    } else {
      console.error("Flag not found for:", countryName);
      setFlagSrc(flags[`./flags/xx.svg`]()); // Fallback if flag is not available
    }
  }, [flagFile, flags]);

  useEffect(() => {
    if (showTooltip && flagRef.current) {
      const rect = flagRef.current.getBoundingClientRect();
      setTooltipStyle({
        top: rect.bottom + 8 + window.scrollY, // Tooltip appears below the flag
        left: rect.left + rect.width / 2 + window.scrollX,
        visibility: 'visible',
      });
    }
  }, [showTooltip]);

  return (
    <>
      <div
        ref={flagRef}
        className="relative inline-block"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {flagSrc ? (
          <img
            src={flagSrc}
            alt={alt}
            className={className}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gray-200"></div> // Placeholder while loading
        )}
      </div>

      {showTooltip &&
        ReactDOM.createPortal(
          <div
            className="fixed whitespace-nowrap bg-black text-white text-xs font-semibold py-1 px-2 rounded-md shadow-lg z-[9999] pointer-events-none transition-opacity duration-200"
            style={{
              position: 'absolute',
              ...tooltipStyle,
              transform: 'translateX(-50%)',
              visibility: showTooltip ? 'visible' : 'hidden',
            }}
          >
            {countryName || 'Country not found'}
          </div>,
          document.body
        )}
    </>
  );
};

export default CountryFlag;
